import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PanelLeftOpenIcon, XIcon } from "lucide-react";

import { ReviewV2Body } from "./ReviewV2Body";
import { Center } from "@/components/Center";
import { Column } from "@/components/Column";
import { LoadingIndicator } from "@/components/LoadingIndicator";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { ReviewPortalToastStack } from "@/components/application-review/ReviewPortalToastStack";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

import { useGetJob } from "@/hooks/queries/useGetJob";
import { useUpdateSetUndoLastCandidacyStatusChange } from "@/hooks/updateSets/useUpdateSetUndoLastCandidacyStatusChange";
import { FLOATING_PROMPT_SELECTOR } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import {
  CandidacyStatus,
  DashboardReviewState,
  ReviewPortalToast,
  ReviewPortalToastKind,
} from "@/types";

// Long enough for Radix's own unmount cleanup to finish first.
const RESTORE_POINTER_EVENTS_DELAY_MS = 100;

// Application review over the V2 candidate sheet. The queue is a frozen
// snapshot of candidacy ids plus the set actioned so far — no query backs it.
export const ApplicationReviewV2Modal = ({
  onAllActioned,
  onOpenChange,
  open,
  state,
  userId,
}: {
  onAllActioned?: () => void;
  onOpenChange: (next: boolean) => void;
  open: boolean;
  state: DashboardReviewState;
  userId: number;
}) => {
  /* Database CRUD Operations */
  // Read
  const { companyId, jobId } = state;
  const { data: job } = useGetJob({ companyId, jobId });
  // Update
  const { mutate: undoLastCandidacyStatusChange } =
    useUpdateSetUndoLastCandidacyStatusChange();

  /* React Hooks */
  const queryClient = useQueryClient();
  const [isJobOverviewCollapsed, setIsJobOverviewCollapsed] = useState(false);
  const [actionedIds, setActionedIds] = useState<Set<string>>(new Set());
  const [index, setIndex] = useState(0);
  const [reviewToasts, setReviewToasts] = useState<ReviewPortalToast[]>([]);
  // Decisions defer their dashboard refetch, so the modal owes one on the way out.
  const hasDeferredDashboardInvalidate = useRef(false);

  // Reset state when the job changes in application review mode.
  useEffect(() => {
    setActionedIds(new Set());
    setIndex(0);
    setReviewToasts([]);
    setIsJobOverviewCollapsed(false);
  }, [state.jobId]);

  // Radix leaves `pointer-events: none` on the body when the dialog unmounts
  // mid-interaction, which is what auto-closing on the last application does —
  // the host page stays unclickable until it's cleared.
  // https://github.com/shadcn-ui/ui/issues/1912#issuecomment-2187447622
  useEffect(() => {
    return () => {
      setTimeout(() => {
        document.body.style.pointerEvents = "";
      }, RESTORE_POINTER_EVENTS_DELAY_MS);
    };
  }, []);

  // Safety net for hosts that drop the modal without routing through close —
  // the deferred dashboard refetch still has to happen.
  useEffect(() => {
    return () => {
      if (!hasDeferredDashboardInvalidate.current) return;
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      void queryClient.invalidateQueries({
        queryKey: ["dashboard-top-candidates"],
      });
    };
  }, [queryClient]);

  /* Helpers */
  const remainingIds = state.candidacyIds.filter((id) => !actionedIds.has(id));
  const total = remainingIds.length;
  const safeIndex = Math.min(index, Math.max(total - 1, 0));
  const candidacyId = total > 0 ? remainingIds[safeIndex] : null;
  const canMoveToPrevious = safeIndex > 0;
  const canMoveToNext = safeIndex < total - 1;

  /* Handlers */
  const flushDeferredDashboardInvalidate = () => {
    if (!hasDeferredDashboardInvalidate.current) return;
    hasDeferredDashboardInvalidate.current = false;
    void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    void queryClient.invalidateQueries({
      queryKey: ["dashboard-top-candidates"],
    });
  };

  const handleClose = () => {
    mixpanelUtils.trackButtonClick(
      "Close application review",
      MIXPANEL_SCREENS.APPLICATION_REVIEW_PORTAL,
    );
    flushDeferredDashboardInvalidate();
    onOpenChange(false);
  };

  const handleMoveToPrevious = () => {
    if (canMoveToPrevious) setIndex(safeIndex - 1);
  };

  const handleMoveToNext = () => {
    if (canMoveToNext) setIndex(safeIndex + 1);
  };

  const handleActioned = ({
    agentActive,
    candidacyId: actionedId,
    candidateName,
    status,
  }: {
    agentActive: boolean;
    candidacyId: string;
    candidateName: string;
    status: CandidacyStatus;
  }) => {
    hasDeferredDashboardInvalidate.current = true;

    const toastKind =
      status === CandidacyStatus.REJECTED
        ? ReviewPortalToastKind.REJECT
        : status === CandidacyStatus.SHORTLIST && agentActive
          ? ReviewPortalToastKind.AGENT_HANDLING
          : null;

    if (toastKind) {
      setReviewToasts((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          candidacyId: actionedId,
          candidateName,
          jobId: state.jobId,
          kind: toastKind,
        },
      ]);
    }

    const newActioned = new Set(actionedIds);
    newActioned.add(actionedId);
    setActionedIds(newActioned);

    const newRemainingCount = total - 1;
    if (newRemainingCount === 0) {
      flushDeferredDashboardInvalidate();
      if (onAllActioned) {
        onAllActioned();
      } else {
        handleClose();
      }
      return;
    }
    if (safeIndex >= newRemainingCount) {
      setIndex(newRemainingCount - 1);
    }
  };

  const handleToastDismiss = (toastId: string) => {
    setReviewToasts((prev) => prev.filter((t) => t.id !== toastId));
  };

  const handleToastUndo = (toast: ReviewPortalToast) => {
    mixpanelUtils.trackButtonClick(
      "Undo Reject Candidacy",
      MIXPANEL_SCREENS.APPLICATION_REVIEW_PORTAL,
    );
    undoLastCandidacyStatusChange(
      { candidacyId: toast.candidacyId },
      {
        onSuccess: () => {
          setActionedIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(toast.candidacyId);

            // Jump to the restored candidate so the user sees who came back.
            const newRemaining = state.candidacyIds.filter(
              (id) => !newSet.has(id),
            );
            const restoredIndex = newRemaining.indexOf(toast.candidacyId);
            if (restoredIndex !== -1) setIndex(restoredIndex);

            return newSet;
          });
          handleToastDismiss(toast.id);
        },
      },
    );
  };

  /* Render */
  const renderHeader = () => {
    return (
      <Row className="shrink-0 items-center justify-between gap-3 border-b border-slate-200/80 bg-slate-100 px-4 py-2.5">
        <Row className="items-center gap-1.5">
          {job && isJobOverviewCollapsed ? (
            <Button
              className="w-8 text-slate-500"
              onClick={() => {
                mixpanelUtils.trackButtonClick(
                  "Expand job overview",
                  MIXPANEL_SCREENS.APPLICATION_REVIEW_PORTAL,
                );
                setIsJobOverviewCollapsed(false);
              }}
              type="button"
              variant="ghost"
            >
              <PanelLeftOpenIcon />
            </Button>
          ) : null}
          <Text className="text-sm font-medium text-slate-500">
            Application Review Mode
          </Text>
        </Row>
        <Button
          className="h-8 w-8 text-slate-500"
          onClick={handleClose}
          size="icon"
          type="button"
          variant="ghost"
        >
          <XIcon className="h-4 w-4" />
        </Button>
      </Row>
    );
  };

  const renderBody = () => {
    if (!job) {
      return (
        <Center className="min-h-0 flex-1">
          <LoadingIndicator variant="large" />
        </Center>
      );
    }

    return (
      <ReviewV2Body
        candidacyId={candidacyId}
        canMoveToNext={canMoveToNext}
        canMoveToPrevious={canMoveToPrevious}
        isJobOverviewCollapsed={isJobOverviewCollapsed}
        job={job}
        onActioned={handleActioned}
        onClose={handleClose}
        onCollapseJobOverview={() => {
          setIsJobOverviewCollapsed(true);
        }}
        onMoveToNext={handleMoveToNext}
        onMoveToPrevious={handleMoveToPrevious}
        safeIndex={safeIndex}
        state={state}
        total={total}
        userId={userId}
      />
    );
  };

  const render = () => {
    return (
      <Dialog
        onOpenChange={(next) => {
          if (!next) handleClose();
          else onOpenChange(next);
        }}
        open={open}
      >
        <DialogContent
          className="flex h-[95vh] w-[92vw] max-w-none flex-col gap-0 overflow-hidden border border-slate-200 bg-slate-100 p-0 shadow-xl sm:rounded-xl"
          hideCloseButton
          // Don't close the modal when interacting with the active screen prompt
          // Ex: notis, screening in progresss
          onInteractOutside={(e) => {
            const target = e.detail.originalEvent.target as HTMLElement | null;
            if (target?.closest(FLOATING_PROMPT_SELECTOR)) {
              e.preventDefault();
            }
          }}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
          }}
        >
          <DialogTitle className="sr-only">
            {job?.title ?? "Application review"}
          </DialogTitle>
          {renderHeader()}
          <Column className="relative min-h-0 flex-1">
            {renderBody()}
            <ReviewPortalToastStack
              onDismiss={handleToastDismiss}
              onUndo={handleToastUndo}
              toasts={reviewToasts}
            />
          </Column>
        </DialogContent>
      </Dialog>
    );
  };

  return render();
};
